import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BarChart, BarChartResponse } from './model/bar-chart';
import { SignalsService } from '@shared/services/signals/signals.service';
import {
  filter,
  map,
  Observable,
  share,
  switchMap,
} from 'rxjs';
import { PieChart, PieChartResponseAircraft, PieChartResponseSeat, TimeByType } from './model/pie-chart';
import { ComboChart, ComboChartResponse } from './model/combo-chart';
import { ApiCacheService } from '@shared/services/cache-service/api-cache.service';

@Injectable({
  providedIn: 'root'
})
export class ChartService {

  documentStyle = getComputedStyle(document.documentElement);

  private readonly _apiUrl = `${environment.apiUrl}`
  constructor(
    private signalService: SignalsService,
    private cacheService: ApiCacheService
  ) { }

  getByYearBarChart(): Observable<BarChart> {
    const apiUrl = `${this._apiUrl}/stats/yearlyFlightTime`;
    const chartType = 'chartYear';

    return this.signalService.employeeNumber$
      .pipe(
        filter((empNum) => empNum !== ''),
        switchMap((empNumber) => {
          const url = `${apiUrl}/${empNumber}`
          const cacheId = `${empNumber}-${chartType}`;

          return this.cacheService.request<BarChartResponse>(cacheId, url, 'data')
            .pipe(
              map((dataResponse) => {
                if (!dataResponse.response || dataResponse.isLoading) {
                  return { labels: [], data: [] };
                }
                let labels: string[] = [];
                let data: number[] = [];
                for (let item of dataResponse.response.data) {
                  labels.push(String(item.year));
                  data.push(this.convertMinutesToHours(item.totalTime));
                }
                let chart: BarChart = { labels: labels, data: data };
                return chart;
              }),
              share()
            );
        })
      );
  }

  getBySeatPieChart(): Observable<PieChart> {
    const apiUrl = `${this._apiUrl}/stats/flightTimeBySeat`;
    const chartType = 'chartSeat';

    return this.signalService.employeeNumber$
      .pipe(
        filter((empNum) => empNum !== ''),
        switchMap((empNumber) => {
          const url = `${apiUrl}/${empNumber}`
          const cacheId = `${empNumber}-${chartType}`;

          return this.cacheService.request<PieChartResponseSeat>(cacheId, url, 'data')
            .pipe(
              map((dataResponse) => {
                if (!dataResponse.response || dataResponse.isLoading) {
                  return { labels: [], data: [] };
                }
                const labels: string[] = [];
                const data: number[] = [];
                for (let item of dataResponse.response.data) {
                  labels.push(item.seat);
                  data.push(this.convertMinutesToHours(item.totalTime));
                }
                let chart: PieChart = { labels: labels, data: data };
                return chart;
              }),
              share()
            );
        })
      );
  }

  getByAircraftPieChart(): Observable<PieChart> {
    const apiUrl = `${this._apiUrl}/Stats/flightTimeByType`;
    const chartType = 'chartAircraft';

    return this.signalService.employeeNumber$
      .pipe(
        filter((empNum) => empNum !== ''),
        switchMap((empNumber) => {
          const url = `${apiUrl}/${empNumber}`
          const cacheId = `${empNumber}-${chartType}`;

          return this.cacheService.request<PieChartResponseAircraft>(cacheId, url, 'data')
            .pipe(
              map((dataResponse) => {
                const labels: string[] = [];
                const totalTime: number[] = [];
                let timeByType: TimeByType[] = [];
                if (!dataResponse.response || dataResponse.isLoading) {
                  return { labels: [], data: [] };
                }
                for (let item of dataResponse.response.data) {
                  labels.push(String(item.typeName));
                  totalTime.push(this.convertMinutesToHours(item.totalTime));
                  timeByType = item.timeByType;
                }
          
                let chart: PieChart = { labels: labels, data: totalTime, timeByType: timeByType };
                return chart;
              }),
              share()
            );
        })
      );
  }


  /**
   * Retrieves a combination line chart data by month that is used to display the chart
   * as well as provide the required data to display the Flight Totals on Flights Table
   * View.  This is why this mehtod requires the isRefresh parameter.  See flightTotalsService
   * 
   * @param {boolean} [isRefresh=false] - Determines whether to force refresh the data.
   * @returns {Observable<ComboChart[]>} - An observable that emits an array of ComboChart objects.
   * 
   * This method constructs an API URL using the employee number obtained from the signalService.
   * It then checks if the data should be refreshed or fetched from the cache.  
   * 
   * If `isRefresh` is false, it attempts to retrieve the data from the cache using the cacheService.
   * If `isRefresh` is true, it forces a refresh and fetches the data from the API.
   * 
   * The data is then transformed using the `transformComboChartData` method before being returned.
   */
  getByMonthLineChartCombo(isRefresh = false): Observable<ComboChart[]> {
    const apiUrl = `${this._apiUrl}/stats/allPeriods`;
    const chartType = 'chartCombo';

    return this.signalService.employeeNumber$
      .pipe(
        filter((empNum) => empNum !== ''),
        switchMap((empNumber) => {
          const url = `${apiUrl}/${empNumber}`
          const cacheId = `${empNumber}-${chartType}`;
          if (!isRefresh) {
            return this.cacheService.request<ComboChartResponse>(cacheId, url, 'data')
              .pipe(
                map((dataResponse) => {
                  if (!dataResponse.response || dataResponse.isLoading) {
                    return [];
                  }
                  return this.transformComboChartData(dataResponse.response);
                }),
                share()
              );
          }
          else {
            return this.cacheService.forceRefreshGet<ComboChartResponse>(cacheId, url, 'data')
              .pipe(
                map((dataResponse) => {
                  if (!dataResponse.response || dataResponse.isLoading) {
                    return [];
                  }
                  //map it here
                  return this.transformComboChartData(dataResponse.response);
                }),
                share()
              );
          }
        })
      );
  }

  transformComboChartData(dataResponse: ComboChartResponse): ComboChart[] {
    const charts: ComboChart[] = [];
    for (let year of dataResponse.data) {
      const labels: number[] = [];
      const data: number[] = [];
      for (let item of year.months) {
        labels.push(item.month);
        data.push(this.convertMinutesToHours(item.totalTime));
      }
      const chart: ComboChart = {
        year: year.year,
        months: {
          label: labels,
          data: data
        }
      };
      charts.push(chart);
    }
    return charts;
  }

  convertMinutesToHoursFor(data: TimeByType[]): TimeByType[] {
    const dataCopy = data;
    for (let item of dataCopy) {
      item.time = this.convertMinutesToHours(item.time);
      return dataCopy;
    }
    return [];
  }

  convertMinutesToHours(minutes: any): number {
    return Number((minutes / 60).toFixed(2));
  }


  emptyChartData = {
    month: [
      'January',
      'Febraury',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ],
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  }

  lightColorBackground(): any[] {
    return [
      '#1c80cf',
      this.documentStyle.getPropertyValue('--p-yellow-500'),
      this.documentStyle.getPropertyValue('--p-green-500'),
      this.documentStyle.getPropertyValue('--p-red-500'),
      this.documentStyle.getPropertyValue('--p-purple-500'),
      this.documentStyle.getPropertyValue('--p-indigo-500'),
      this.documentStyle.getPropertyValue('--p-blue-500'),
      this.documentStyle.getPropertyValue('--p-cyan-500'),
      this.documentStyle.getPropertyValue('--p-pink-500'),
      this.documentStyle.getPropertyValue('--p-teal-500'),
      this.documentStyle.getPropertyValue('--p-orange-500'),
      this.documentStyle.getPropertyValue('--p-bluegray-500'),
      this.documentStyle.getPropertyValue('--p-primary-500'),
      this.documentStyle.getPropertyValue('--p-gray-500'),
      this.documentStyle.getPropertyValue('--p-surface-500'),
      this.documentStyle.getPropertyValue('--p-yellow-900'),
      this.documentStyle.getPropertyValue('--p-green-900'),
      this.documentStyle.getPropertyValue('--p-red-900'),
      this.documentStyle.getPropertyValue('--p-purple-900'),
      this.documentStyle.getPropertyValue('--p-indigo-900'),
    ];
  }

  darkColorBackground(): any[] {
    return [
      '#1c80cf50',
      this.documentStyle.getPropertyValue('--p-yellow-700'),
      this.documentStyle.getPropertyValue('--p-green-700'),
      this.documentStyle.getPropertyValue('--p-red-700'),
      this.documentStyle.getPropertyValue('--p-purple-700'),
      this.documentStyle.getPropertyValue('--p-indigo-700'),
      this.documentStyle.getPropertyValue('--p-blue-700'),
      this.documentStyle.getPropertyValue('--p-cyan-700'),
      this.documentStyle.getPropertyValue('--p-pink-700'),
      this.documentStyle.getPropertyValue('--p-teal-700'),
      this.documentStyle.getPropertyValue('--p-orange-700'),
      this.documentStyle.getPropertyValue('--p-bluegray-700'),
      this.documentStyle.getPropertyValue('--p-primary-700'),
      this.documentStyle.getPropertyValue('--p-gray-700'),
      this.documentStyle.getPropertyValue('--p-surface-700'),
      this.documentStyle.getPropertyValue('--p-yellow-900'),
      this.documentStyle.getPropertyValue('--p-green-900'),
      this.documentStyle.getPropertyValue('--p-red-900'),
      this.documentStyle.getPropertyValue('--p-purple-900'),
      this.documentStyle.getPropertyValue('--p-indigo-900'),
    ]
  }

  hoverColorBackground(): any[] {
    return [
      '#75bef8',
      this.documentStyle.getPropertyValue('--p-yellow-400'),
      this.documentStyle.getPropertyValue('--p-green-400'),
      this.documentStyle.getPropertyValue('--p-red-400'),
      this.documentStyle.getPropertyValue('--p-purple-400'),
      this.documentStyle.getPropertyValue('--p-indigo-400'),
      this.documentStyle.getPropertyValue('--p-blue-400'),
      this.documentStyle.getPropertyValue('--p-cyan-400'),
      this.documentStyle.getPropertyValue('--p-pink-400'),
      this.documentStyle.getPropertyValue('--p-teal-400'),
      this.documentStyle.getPropertyValue('--p-orange-400'),
      this.documentStyle.getPropertyValue('--p-bluegray-400'),
      this.documentStyle.getPropertyValue('--p-primary-400'),
      this.documentStyle.getPropertyValue('--p-gray-400'),
      this.documentStyle.getPropertyValue('--p-surface-400'),
      this.documentStyle.getPropertyValue('--p-yellow-900'),
      this.documentStyle.getPropertyValue('--p-green-900'),
      this.documentStyle.getPropertyValue('--p-red-900'),
      this.documentStyle.getPropertyValue('--p-purple-900'),
      this.documentStyle.getPropertyValue('--p-indigo-900'),
    ]
  }

}

